import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, Briefcase, ClipboardCheck, Youtube, PlayCircle, Wallet, CheckCircle2, CircleDollarSign, ListVideo } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { groupEventsByDate, filterVisibleEvents, shiftMonth, buildMonthGrid, buildWeekDays, monthLabel, countByType } from "@/utils/calendarUtils";
import { fmtMoney, fmtNum } from "@/utils/currency";

const TYPE_META = {
  deadline: { label: "Job deadline", short: "Deadline", cls: "border-amber-500/30 bg-amber-500/15 text-amber-300", dot: "text-amber-400", icon: Briefcase },
  claimed: { label: "Job claimed", short: "Claimed", cls: "border-sky-500/30 bg-sky-500/15 text-sky-300", dot: "text-sky-400", icon: PlayCircle },
  job_completed: { label: "Job completed", short: "Completed", cls: "border-cyan-500/30 bg-cyan-500/15 text-cyan-300", dot: "text-cyan-400", icon: CheckCircle2 },
  submission: { label: "Submission", short: "Submitted", cls: "border-violet-500/30 bg-violet-500/15 text-violet-300", dot: "text-violet-400", icon: ClipboardCheck },
  video_completed: { label: "Video completed", short: "Video done", cls: "border-teal-500/30 bg-teal-500/15 text-teal-300", dot: "text-teal-400", icon: ListVideo },
  publish: { label: "Publish date", short: "Publish", cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300", dot: "text-emerald-400", icon: Youtube },
  payment_owed: { label: "Payment owed", short: "Owed", cls: "border-rose-500/30 bg-rose-500/15 text-rose-300", dot: "text-rose-400", icon: CircleDollarSign },
  payment_paid: { label: "Payment paid", short: "Paid", cls: "border-lime-500/30 bg-lime-500/15 text-lime-300", dot: "text-lime-400", icon: Wallet },
};
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const VIEWS = ["Day", "Week", "Month", "Timeline"];

// types that always represent finished activity — rendered muted so history doesn't look actionable
const DONE_TYPES = new Set(["job_completed", "video_completed", "payment_paid"]);

const EventChip = ({ e, onOpen, full = false }) => {
  const meta = TYPE_META[e.type] || TYPE_META.deadline;
  const Icon = meta.icon;
  return (
    <button
      data-testid={`calendar-event-${e.type}-${e.id}`}
      onClick={() => onOpen(e)}
      title={`${meta.label}: ${e.title}`}
      className={`${full ? "flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left" : "flex w-full items-center gap-1 truncate rounded border px-1 py-0.5 text-left"} text-[9px] font-semibold transition-transform hover:scale-[1.02] ${meta.cls}`}
    >
      <Icon className={full ? "h-3.5 w-3.5 shrink-0" : "h-2.5 w-2.5 shrink-0"} />
      <span className={full ? "min-w-0 flex-1 truncate text-xs" : "truncate"}>{e.title}</span>
      {full && (
        <span className="shrink-0 text-[10px] opacity-80">
          {[e.worker, e.status && e.status.replace(/_/g, " "), e.amount != null ? fmtMoney(e.amount, 0) : null].filter(Boolean).join(" · ")}
        </span>
      )}
    </button>
  );
};

/**
 * Compact in-cell indicator for Month view: category icon + short label,
 * hover popover (title/type/worker/amount/date), click opens the source record.
 * Past/completed events are muted; open/upcoming ones stay full-strength.
 */
const DayIndicator = ({ e, today, onOpen }) => {
  const meta = TYPE_META[e.type] || TYPE_META.deadline;
  const Icon = meta.icon;
  const done = DONE_TYPES.has(e.type) || e.date < today;
  const short = e.amount != null && e.type.startsWith("payment") ? `${meta.short} ${fmtMoney(e.amount, 0)}` : meta.short;
  return (
    <span className="group relative block" data-testid={`calendar-indicator-wrap-${e.type}-${e.id}`}>
      <button
        data-testid={`calendar-indicator-${e.type}-${e.id}`}
        onClick={(ev) => { ev.stopPropagation(); onOpen(e); }}
        className={`flex w-full items-center gap-1 rounded border px-1 py-0.5 text-left text-[9px] font-semibold transition-transform hover:scale-[1.03] ${meta.cls} ${done ? "opacity-50 saturate-50" : ""}`}
      >
        <Icon className="h-2.5 w-2.5 shrink-0" />
        <span className="truncate">{short}</span>
      </button>
      <span
        data-testid={`calendar-popover-${e.type}-${e.id}`}
        className="pointer-events-none absolute bottom-full left-1/2 z-[110] mb-1 hidden w-44 -translate-x-1/2 rounded-xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-2.5 text-left shadow-2xl group-hover:block"
      >
        <span className="block text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">{meta.label}{done ? " · done/past" : " · upcoming"}</span>
        <span className="mt-0.5 block break-words text-[11px] font-bold text-[var(--pv-text)]">{e.title}</span>
        <span className="mt-0.5 block text-[10px] text-[var(--pv-text-3)]">
          {[e.worker, e.status && e.status.replace(/_/g, " "), e.amount != null ? fmtMoney(e.amount, 0) : null, e.date].filter(Boolean).join(" · ")}
        </span>
      </span>
    </span>
  );
};

export const AdminCalendar = ({ onNavigate }) => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [events, setEvents] = useState(null);
  const [selected, setSelected] = useState(() => new Date().toISOString().slice(0, 10));
  const [view, setView] = useState("Month");
  const [hidden, setHidden] = useState(new Set());

  const load = useCallback(() => {
    setEvents(null);
    axios.get(`${API}/staff/calendar?month=${month}`, { headers: authHeaders() })
      .then((r) => setEvents(r.data.events))
      .catch(() => setEvents([]));
  }, [month]);
  useEffect(load, [load]);

  const shift = (delta) => setMonth((cur) => shiftMonth(cur, delta));

  const open = (e) => onNavigate?.(e.section, e.focus_testid);

  const visible = useMemo(() => filterVisibleEvents(events, hidden), [events, hidden]);
  const byDate = useMemo(() => groupEventsByDate(visible), [visible]);

  const label = monthLabel(month);
  const today = new Date().toISOString().slice(0, 10);

  const monthGrid = useMemo(() => buildMonthGrid(month), [month]);

  const weekDays = useMemo(() => buildWeekDays(selected, month), [selected, month]);

  const toggleType = (t) => setHidden((h) => { const n = new Set(h); n.has(t) ? n.delete(t) : n.add(t); return n; });

  const counts = useMemo(() => countByType(events), [events]);

  return (
    <div data-testid="calendar-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-500/15 text-cyan-300">
            <CalendarDays className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Production calendar</h1>
            <p className="mt-1 text-sm text-[var(--pv-text-4)]">Every dated record in the studio — deadlines, claims, submissions, completions, publish dates, payments.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div data-testid="calendar-view-switch" className="flex rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-1">
            {VIEWS.map((v) => (
              <button key={v} data-testid={`calendar-view-${v.toLowerCase()}`} onClick={() => setView(v)}
                className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold transition-colors ${view === v ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)] hover:text-[var(--pv-text)]"}`}>
                {v}
              </button>
            ))}
          </div>
          <button data-testid="calendar-prev" onClick={() => shift(-1)} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-2 text-[var(--pv-text-3)] hover:text-cyan-400"><ChevronLeft className="h-4 w-4" /></button>
          <p data-testid="calendar-month-label" className="w-36 text-center font-display text-sm font-bold text-[var(--pv-text)]">{label}</p>
          <button data-testid="calendar-next" onClick={() => shift(1)} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-2 text-[var(--pv-text-3)] hover:text-cyan-400"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div data-testid="calendar-filters" className="mt-4 flex flex-wrap gap-1.5">
        {Object.entries(TYPE_META).map(([t, meta]) => {
          if (!counts[t]) return null;
          const off = hidden.has(t);
          return (
            <button key={t} data-testid={`calendar-filter-${t}`} onClick={() => toggleType(t)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold transition-opacity ${meta.cls} ${off ? "opacity-30" : ""}`}>
              <meta.icon className="h-3 w-3" /> {meta.label} ({counts[t]})
            </button>
          );
        })}
      </div>

      {!events ? (
        <p data-testid="calendar-loading" className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : view === "Timeline" ? (
        <div data-testid="calendar-timeline" className="mt-6 space-y-2">
          {visible.length === 0 ? (
            <p data-testid="calendar-empty" className="rounded-3xl border border-dashed border-[var(--pv-border-strong)] py-14 text-center text-sm text-[var(--pv-text-4)]">No dated records this month.</p>
          ) : (
            visible.map((e, i) => (
              <div key={`${e.type}-${e.id}-${i}`} className="flex items-center gap-3">
                <span className="w-20 shrink-0 text-right font-mono-label text-[10px] font-bold uppercase text-[var(--pv-text-4)]">{e.date.slice(8)} {new Date(`${e.date}T00:00:00Z`).toLocaleString(undefined, { weekday: "short", timeZone: "UTC" })}</span>
                <div className="w-full"><EventChip e={e} onOpen={open} full /></div>
              </div>
            ))
          )}
        </div>
      ) : view === "Day" ? (
        <div className="mt-6 grid gap-4 lg:grid-cols-[220px_1fr]">
          <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Pick a day</p>
            <div className="mt-2 grid grid-cols-7 gap-1">
              {monthGrid.filter(Boolean).map((day) => (
                <button key={day} data-testid={`calendar-daypick-${day}`} onClick={() => setSelected(day)}
                  className={`rounded-lg py-1.5 text-[11px] font-bold ${selected === day ? "bg-cyan-500 text-white" : byDate[day] ? "bg-[var(--pv-subtle)] text-[var(--pv-text-2)]" : "text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"}`}>
                  {Number(day.slice(-2))}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5">
            <p data-testid="calendar-day-label" className="font-display text-base font-bold text-[var(--pv-text)]">
              {new Date(`${selected}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })}
            </p>
            <div data-testid="calendar-day-events" className="mt-3 space-y-2">
              {(byDate[selected] || []).length === 0 ? (
                <p className="text-xs text-[var(--pv-text-4)]">Nothing scheduled — select another day.</p>
              ) : (
                (byDate[selected] || []).map((e, i) => <EventChip key={i} e={e} onOpen={open} full />)
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4">
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
            {WEEKDAYS.map((d) => <div key={d} className="py-1.5">{d}</div>)}
          </div>
          {view === "Month" ? (
            <div data-testid="calendar-grid" className="grid grid-cols-7 gap-1">
              {monthGrid.map((day, i) =>
                day === null ? <div key={`pad-${i}`} /> : (
                  <div key={day} data-testid={`calendar-day-${day}`}
                    role="button" tabIndex={0}
                    onClick={() => { setSelected(day); setView("Day"); }}
                    onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); setSelected(day); setView("Day"); } }}
                    className={`min-h-16 cursor-pointer rounded-xl border p-1.5 text-left align-top transition-colors sm:min-h-20 ${
                      day === today ? "border-cyan-500/40 bg-cyan-500/5" : "border-[var(--pv-border)] bg-[var(--pv-bg)] hover:border-[var(--pv-border-strong)]"
                    }`}>
                    <span className={`text-[11px] font-bold ${day === today ? "text-cyan-400" : "text-[var(--pv-text-3)]"}`}>{Number(day.slice(-2))}</span>
                    {/* desktop: icon + short label per event, first 3 then +N more */}
                    <div className="mt-1 hidden space-y-0.5 sm:block">
                      {(byDate[day] || []).slice(0, 3).map((e, j) => <DayIndicator key={`${e.type}-${e.id}-${j}`} e={e} today={today} onOpen={open} />)}
                      {(byDate[day] || []).length > 3 && (
                        <span data-testid={`calendar-more-${day}`} className="block px-1 text-[9px] font-bold text-[var(--pv-text-4)]">+{byDate[day].length - 3} more</span>
                      )}
                    </div>
                    {/* small screens: compact category icon row so busy days are still visible */}
                    {(byDate[day] || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap items-center gap-0.5 sm:hidden" data-testid={`calendar-dots-${day}`}>
                        {(byDate[day] || []).slice(0, 5).map((e, j) => {
                          const meta = TYPE_META[e.type] || TYPE_META.deadline;
                          const MIcon = meta.icon;
                          const done = DONE_TYPES.has(e.type) || e.date < today;
                          return <MIcon key={j} className={`h-2.5 w-2.5 ${meta.dot} ${done ? "opacity-50" : ""}`} />;
                        })}
                        {(byDate[day] || []).length > 5 && <span className="text-[8px] font-bold text-[var(--pv-text-4)]">+{byDate[day].length - 5}</span>}
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          ) : (
            <div data-testid="calendar-week" className="grid grid-cols-7 gap-1">
              {weekDays.map((day) => (
                <div key={day} data-testid={`calendar-weekday-${day}`}
                  className={`min-h-32 rounded-xl border p-1.5 ${day === today ? "border-cyan-500/40 bg-cyan-500/5" : "border-[var(--pv-border)] bg-[var(--pv-bg)]"} ${!day.startsWith(month) ? "opacity-40" : ""}`}>
                  <button onClick={() => { setSelected(day); setMonth(day.slice(0, 7)); setView("Day"); }} className="text-[11px] font-bold text-[var(--pv-text-3)] hover:text-cyan-400">{Number(day.slice(-2))}</button>
                  <div className="mt-1 space-y-0.5">
                    {(byDate[day] || []).map((e, j) => <EventChip key={j} e={e} onOpen={open} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
